(() => {
  const state = { offers: [], sliderGifts: [], addGift: null, cart: null, busy: false };
  const views = new Set();
  let refreshTimer;
  let refreshRequest;
  let cartRevision = 0;
  let viewId = 0;
  let pendingEligibility;
  const root = () => document.querySelector('bogos-gifts')?.dataset.root?.replace(/\/?$/, '/') || '/';
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[char]));
  const image = (value) => {
    try {
      const url = new URL(value, location.origin);
      return /^(https?:)$/.test(url.protocol) ? escape(url.href) : '';
    } catch { return ''; }
  };
  const offerId = (gift) => String(gift.belongs_to_offer ?? 'default');
  const groupGifts = (gifts) => {
    const groups = new Map();
    gifts.forEach((gift) => {
      const key = gift.handle || gift.product_id || gift.id || gift.title;
      const group = groups.get(key) || { ...gift, variants: [] };
      (gift.variants || []).forEach((variant) => {
        if (!group.variants.some((existing) => existing.id === variant.id && existing.title === variant.title)) group.variants.push(variant);
      });
      groups.set(key, group);
    });
    return [...groups.values()];
  };
  const isGift = (item) => Object.keys(item.properties || {}).some((key) =>
    /free.?gift|bogos|tier_reward/i.test(key)) || /SECOMAPP_FREEGIFT/.test(item.vendor || '');
  const claimed = (offer) => state.cart?.items?.some((item) =>
    String(item.properties?._bogos_trigger_id) === String(offer.id) ||
    ((offer.gifts || []).some((gift) => (gift.variants || []).some((variant) =>
      String(item.variant_id) === String(variant.id))) && isGift(item)));
  const offers = () => {
    if (state.offers.length) return state.offers.map((offer) => ({ ...offer,
      gifts: state.sliderGifts.some((gift) => offerId(gift) === String(offer.id))
        ? state.sliderGifts.filter((gift) => offerId(gift) === String(offer.id)) : offer.gifts || [],
    }));
    const groups = new Map();
    state.sliderGifts.forEach((gift) => {
      const id = offerId(gift);
      if (!groups.has(id)) groups.set(id, { id, title: '', success: true, gifts: [] });
      groups.get(id).gifts.push(gift);
    });
    return [...groups.values()];
  };
  const thresholdFor = (offer, currency) => {
    const app = window.FGSECOMAPP;
    const config = app?.offers?.find((item) => String(item.id) === String(offer.id));
    const condition = config?.offer_conditions?.find((item) => item.unit === 'money');
    if (!condition) return null;
    const converted = condition.other?.currencies?.find((item) => item.currency === currency);
    const shopCurrency = document.querySelector('bogos-gifts')?.dataset.shopCurrency || 'USD';
    const rate = Number(window.Shopify?.currency?.rate);
    // BOGOS rewrites min_amount into its cart currency before emitting offers.
    const sourceCurrency = app?.SHOPIFY_CART?.currency || shopCurrency;
    const amount = currency === sourceCurrency ? condition.min_amount : converted?.min_amount ??
      (sourceCurrency === shopCurrency && window.Shopify?.currency?.active === currency && rate > 0
        ? condition.min_amount * rate : null);
    return Number(amount) > 0 ? Number(amount) : null;
  };
  const renderProgress = () => {
    document.querySelectorAll('[data-gift-progress]').forEach((holder) => {
      const currency = state.cart?.currency || holder.dataset.currency || 'USD';
      const money = (amount) => new Intl.NumberFormat(holder.dataset.locale, { style: 'currency', currency }).format(amount);
      const tiers = offers().map((offer) => ({ offer, threshold: thresholdFor(offer, currency) }))
        .filter((tier) => tier.threshold !== null).sort((a, b) => a.threshold - b.threshold);
      holder.hidden = !tiers.length;
      const subtotal = (state.cart?.items || []).filter((item) => !isGift(item))
        .reduce((sum, item) => sum + item.final_line_price, 0) / 100;
      const milestones = tiers.map(({ offer, threshold }) => {
        const inCart = claimed(offer);
        const unlocked = inCart || (!!state.cart?.item_count && offer.success === true);
        const app = window.FGSECOMAPP;
        const config = app?.offers?.find((item) => String(item.id) === String(offer.id));
        const current = state.cart && app?.SHOPIFY_CART?.currency === currency && app.SHOPIFY_CART.total_price === state.cart.total_price && app.SHOPIFY_CART.item_count === state.cart.item_count;
        const countdown = config?.count_down_value;
        const remaining = Math.max(0, current && Number.isFinite(countdown) ? countdown : threshold - subtotal);
        return { offer, threshold, unlocked, remaining };
      });
      if (!milestones.length) { holder.innerHTML = ''; return; }
      const nextIndex = milestones.findIndex((tier) => !tier.unlocked);
      const complete = nextIndex === -1;
      const next = milestones[complete ? milestones.length - 1 : nextIndex];
      const previous = milestones[(complete ? milestones.length : nextIndex) - 1];
      const start = !complete && previous ? previous.threshold : 0;
      const progress = complete ? 100 : Math.max(0, Math.min(100,
        (next.threshold - next.remaining - start) / (next.threshold - start) * 100));
      const message = complete ? holder.dataset.complete : holder.dataset.next.replace('{{ amount }}', money(next.remaining));
      const reached = previous ? holder.dataset.reached.replace('{{ amount }}', money(previous.threshold)) : '';
      const target = holder.dataset.target.replace('{{ amount }}', money(next.threshold));
      holder.innerHTML = `<div class="bogos-progress__header">
        <span class="bogos-progress__message">${escape(message)}</span>
        <span class="bogos-progress__state">${escape(complete ? holder.dataset.unlocked : holder.dataset.locked)}</span></div>
        <div class="bogos-progress__track" role="progressbar" aria-label="${escape(target)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress)}"><span style="width:${progress}%"></span></div>
        <div class="bogos-progress__footer"><span>${escape(reached)}</span><span>${escape(complete ? '' : target)}</span></div>`;
    });
  };
  const render = () => {
    views.forEach((view) => view.render());
    renderProgress();
    document.body?.classList.toggle('ditch-bogos-active', [...views].some((view) => !view.hidden));
  };
  async function readCart() {
    const revision = cartRevision;
    const response = await fetch(`${root()}cart.js`);
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (revision === cartRevision) state.cart = cart;
    return cart;
  }
  async function refreshCarts() {
    if (refreshRequest) return refreshRequest;
    refreshRequest = (async () => {
      await readCart();
      const carts = [...document.querySelectorAll('cart-drawer, cart-page')];
      const ids = [...new Set(carts.map((cart) => cart.dataset.sectionId).filter(Boolean))];
      if (ids.length) {
        const focused = document.activeElement?.closest('bogos-gifts');
        const focusedCart = focused?.closest('cart-drawer, cart-page');
        const focusedOffer = document.activeElement?.closest('[data-offer]')?.dataset.offer;
        const response = await fetch(`${root()}?${new URLSearchParams({ sections: ids.join(',') })}`);
        if (!response.ok) throw new Error('Cart sections unavailable');
        const sections = await response.json();
        carts.forEach((cart) => cart.refresh?.(sections));
        if (focusedCart) {
          const summary = [...focusedCart.querySelectorAll('.bogos-gifts__offer')]
            .find((offer) => offer.dataset.offer === focusedOffer)?.querySelector('[data-open-offer]');
          (summary || focusedCart.querySelector('[data-cart-close]'))?.focus();
        }
      }
      render();
    })().finally(() => { refreshRequest = null; });
    return refreshRequest;
  }
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refreshCarts().catch(() => {
      views.forEach((view) => view.status(view.labels.error));
    }), 150);
  };

  // Replay early events captured before the deferred selector script loaded.
  document.addEventListener('fg-today-offer:render', (event) => {
    if (!Array.isArray(event.detail?.todayOffers)) return;
    state.offers = event.detail.todayOffers;
    render();
  });
  document.addEventListener('fg-gifts:show-slider', (event) => {
    if (!Array.isArray(event.detail?.gifts)) return;
    state.sliderGifts = event.detail.gifts;
    state.offers = state.offers.map((offer) => ({ ...offer,
      success: state.sliderGifts.some((gift) => offerId(gift) === String(offer.id)) || offer.success,
    }));
    if (typeof event.detail.addGiftToCartFunc === 'function') state.addGift = event.detail.addGiftToCartFunc;
    render();
  });
  document.addEventListener('fg-today-offer:success', (event) => {
    const ids = event.detail?.idOffersSuccess;
    if (!Array.isArray(ids)) return;
    state.offers = state.offers.map((offer) => ({ ...offer, success: ids.map(String).includes(String(offer.id)) }));
    render();
    scheduleRefresh();
  });
  ['fg-gifts:updated', 'fg-cart:auto-updated', 'fg-gifts:added'].forEach((name) =>
    document.addEventListener(name, scheduleRefresh));
  document.addEventListener('ditch:cart-updated', (event) => {
    if (event.detail?.phase === 'failed') {
      const previous = pendingEligibility;
      const revision = cartRevision;
      const signature = (cart) => JSON.stringify(cart?.items?.map((item) => [item.variant_id, item.quantity, item.final_line_price]));
      readCart().then((cart) => {
        if (revision !== cartRevision) return;
        if (previous && signature(cart) === signature(previous.cart)) {
          state.offers = previous.offers;
          state.sliderGifts = previous.sliderGifts;
        }
        render();
      }).catch(() => {});
      return;
    }
    if (event.detail?.phase === 'complete') {
      state.cart = event.detail.cart || null;
      readCart().then(render).catch(() => {});
      return;
    }
    pendingEligibility = { cart: state.cart, offers: state.offers, sliderGifts: state.sliderGifts };
    cartRevision++;
    state.cart = event.detail?.cart || state.cart;
    // Old slider data is no longer proof of eligibility after a shopper changes the cart.
    state.sliderGifts = [];
    state.offers = state.offers.map((offer) => ({ ...offer, success: false }));
    render();
  });

  class BogosGifts extends HTMLElement {
    connectedCallback() {
      this.viewId = ++viewId;
      this.labels = JSON.parse(this.querySelector('[data-gift-labels]').textContent);
      this.list = this.querySelector('[data-gift-offers]');
      this.selection = this.querySelector('[data-gift-selection]');
      this.dropdown = this.querySelector('.bogos-gifts__dropdown');
      this.onChange = (event) => {
        const product = event.target.closest('.bogos-gifts__product');
        if (product) product.querySelector('[data-add-gift]').disabled = state.busy || product.dataset.canClaim !== 'true';
      };
      this.onClick = (event) => {
        if (event.target.closest('.bogos-gifts__heading')) {
          event.preventDefault();
          this.toggleDropdown();
          return;
        }
        const card = event.target.closest('[data-open-offer]');
        if (card) {
          const offer = card.closest('.bogos-gifts__offer');
          this.selectedOfferId = offer.dataset.offer;
          this.list.hidden = true;
          this.querySelector('[data-gift-intro]').hidden = true;
          this.selection.hidden = false;
          this.selection.showModal();
          this.loadProducts(offer);
          this.selection.querySelector('[data-close-gift]').focus();
          return;
        }
        const close = event.target.closest('[data-close-gift]');
        if (close) {
          this.closeSelection();
          return;
        }
        const button = event.target.closest('[data-add-gift]');
        if (button) this.add(button);
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.onCancel = (event) => { event.preventDefault(); this.closeSelection(); };
      this.selection.addEventListener('cancel', this.onCancel);
      views.add(this);
      this.render();
      renderProgress();
      if (!state.cart) readCart().then(render).catch(() => {});
    }
    disconnectedCallback() {
      views.delete(this);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.selection.removeEventListener('cancel', this.onCancel);
      this.dropdownAnimation?.cancel();
      if (this.selection.open) this.selection.close();
    }
    status(message) { this.querySelectorAll('[data-gift-status]').forEach((holder) => { holder.textContent = message; }); }
    toggleDropdown() {
      const dropdown = this.dropdown;
      const expand = !(this.dropdownTarget ?? dropdown.open);
      const start = dropdown.getBoundingClientRect().height;
      this.dropdownAnimation?.cancel();
      this.dropdownTarget = expand;
      dropdown.dataset.expanded = String(expand);
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dropdown.open = expand;
        dropdown.style.removeProperty('overflow');
        delete dropdown.dataset.expanded;
        this.dropdownTarget = null;
        this.dropdownAnimation = null;
        return;
      }
      dropdown.open = true;
      const headingHeight = dropdown.querySelector('summary').getBoundingClientRect().height;
      const end = expand ? headingHeight + dropdown.querySelector('.bogos-gifts__body').getBoundingClientRect().height : headingHeight;
      dropdown.style.overflow = 'hidden';
      const animation = dropdown.animate([{ height: `${start}px` }, { height: `${end}px` }], {
        duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'forwards',
      });
      this.dropdownAnimation = animation;
      animation.finished.then(() => {
        if (this.dropdownAnimation !== animation) return;
        dropdown.open = expand;
        dropdown.style.removeProperty('overflow');
        delete dropdown.dataset.expanded;
        this.dropdownTarget = null;
        this.dropdownAnimation = null;
        animation.cancel();
      }, () => {});
    }
    closeSelection() {
      const selectedId = this.selectedOfferId;
      this.selectedOfferId = null;
      this.selection.close();
      this.selection.hidden = true;
      this.list.hidden = false;
      this.querySelector('[data-gift-intro]').hidden = !!state.cart?.item_count;
      [...this.list.querySelectorAll('.bogos-gifts__offer')].find((offer) => offer.dataset.offer === selectedId)?.querySelector('[data-open-offer]').focus();
    }
    render() {
      const available = offers().filter((offer) => offer.gifts.length).sort((a, b) => (thresholdFor(a, this.dataset.currency) || Infinity) - (thresholdFor(b, this.dataset.currency) || Infinity));
      this.hidden = !available.length;
      if (this.hidden) return;
      const empty = !(state.cart ? state.cart.item_count : Number(this.dataset.cartCount));
      this.querySelector('[data-gift-intro]').hidden = !empty || !!this.selectedOfferId;
      const signature = JSON.stringify([empty, this.dataset.currency, available.map((offer) => [offer, claimed(offer), thresholdFor(offer, this.dataset.currency)])]);
      if (signature === this.signature) return;
      this.signature = signature;
      this.list.innerHTML = available.map((offer) => {
        const inCart = claimed(offer);
        const unlocked = !empty && offer.success === true;
        const gifts = groupGifts(offer.gifts);
        const previews = gifts.slice(0, 3).map((gift, index) => {
          const src = image(gift.thumbnail || gift.variants?.[0]?.thumbnail || '');
          return src ? `<img class="bogos-gifts__preview" style="--preview-index:${index}" src="${src}" alt="${escape(gift.title)}" width="48" height="60" loading="lazy">` : '';
        }).join('');
        const appOffer = window.FGSECOMAPP?.offers?.find((item) => String(item.id) === String(offer.id));
        const threshold = thresholdFor(offer, this.dataset.currency);
        const money = (amount) => new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency || 'USD' }).format(amount);
        const title = threshold ? this.labels.tier.replace('{{ amount }}', money(threshold)) : String(appOffer?.title || offer.title || this.labels.offer).replace(/🎁/g, '').trim();
        return `<section class="bogos-gifts__offer ${inCart ? 'is-claimed' : unlocked ? '' : 'is-locked'}" data-offer="${escape(offer.id)}">
          <div class="bogos-gifts__summary">
            <div class="bogos-gifts__previews">${previews}</div>
            <span class="bogos-gifts__offer-info"><span class="bogos-gifts__title">${escape(title.replace(/\s*--\s*/g, ' · '))}</span>
            <span class="bogos-gifts__browse">${gifts.length} ${escape(gifts.length === 1 ? this.labels.option : this.labels.options)}</span></span>
          </div>
          <button class="bogos-gifts__offer-action" type="button" data-open-offer aria-label="${escape(`${this.labels.browse}: ${title}. ${inCart ? this.labels.claimed : unlocked ? this.labels.unlocked : this.labels.locked}`)}"><span>${escape(inCart ? this.labels.claimed : unlocked ? this.labels.choose : this.labels.locked)}</span><svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">${!unlocked && !inCart ? '<rect x="4" y="7" width="8" height="6" rx="1"/><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"/>' : '<path d="m6 4 4 4-4 4"/>'}</svg></button></section>`;
      }).join('');
      if (this.selectedOfferId) {
        const selected = [...this.list.querySelectorAll('[data-offer]')].find((card) => card.dataset.offer === this.selectedOfferId);
        if (selected) this.loadProducts(selected);
        else this.closeSelection();
      }
    }
    async loadProducts(detail) {
      const requestId = this.productRequest = (this.productRequest || 0) + 1;
      const selectedId = detail.dataset.offer;
      const offer = offers().find((offer) => String(offer.id) === detail.dataset.offer);
      if (!offer) return;
      const holder = this.selection.querySelector('[data-gift-products]');
      holder.setAttribute('aria-busy', 'true');
      holder.textContent = this.labels.loading;
      try {
        const products = await Promise.all(groupGifts(offer.gifts).map(async (gift) => {
          if (!gift.handle) return gift;
          let product;
          try {
            const response = await fetch(`${root()}products/${encodeURIComponent(gift.handle)}.js`);
            if (!response.ok) throw new Error('Gift inventory unavailable');
            product = await response.json();
          } catch {
            return { ...gift, variants: gift.variants.map((variant) => ({ ...variant, available: false })) };
          }
          return { ...gift, variants: gift.variants.map((variant) => {
            const match = product.variants.find((item) => String(item.id) === String(variant.id)) ||
              (!variant.id ? product.variants.find((item) => item.title === variant.title) : null);
            return { ...variant, id: variant.id || match?.id, available: match?.available === true };
          }) };
        }));
        if (this.selectedOfferId !== selectedId || this.productRequest !== requestId) return;
        holder.innerHTML = products.map((gift, index) => this.productHTML(gift, index, offer.id)).join('');
      } catch {
        if (this.selectedOfferId === selectedId && this.productRequest === requestId) {
          holder.textContent = this.labels.error;
          this.status(this.labels.error);
        }
      } finally { if (this.selectedOfferId === selectedId && this.productRequest === requestId) holder.removeAttribute('aria-busy'); }
    }
    productHTML(gift, index, id) {
      const offer = offers().find((item) => String(item.id) === String(id));
      const canClaim = !!state.cart?.item_count && offer?.success === true && !claimed(offer);
      const variants = gift.variants.filter((variant) => variant.id);
      const src = image(gift.thumbnail || variants[0]?.thumbnail || '');
      const name = String(gift.title || '').replace(/🎁/g, '').trim();
      const one = variants.length === 1;
      const available = variants.some((variant) => variant.available !== false);
      const price = Number(variants[0]?.price || 0);
      const money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency || 'USD' }).format(price);
      return `<div class="bogos-gifts__product" data-offer="${escape(id)}" data-can-claim="${canClaim}">
        ${src ? `<img class="bogos-gifts__image" src="${src}" alt="${escape(name)}" width="80" height="107" loading="lazy">` : '<span></span>'}
        <div><h3 class="bogos-gifts__name">${escape(name)}</h3><p class="bogos-gifts__price">${escape(money)}</p>
        <fieldset class="bogos-gifts__options" ${one && variants[0]?.title === 'Default Title' ? 'hidden' : ''}><legend>${escape(this.labels.select)}</legend>
        ${variants.map((variant) => `<label class="bogos-gifts__option"><input type="radio" name="gift-${this.viewId}-${escape(id)}-${index}" value="${escape(variant.id)}" ${variant.available === false ? 'disabled' : one ? 'checked' : ''}><span>${escape(variant.title || 'Default Title')}</span></label>`).join('')}
        </fieldset><button type="button" class="button button--solid type-button" aria-label="${escape(this.labels.add)}: ${escape(name)}" data-add-gift ${!canClaim || !one || !available || state.busy ? 'disabled' : ''}>${escape(!available ? this.labels.soldOut : !canClaim ? claimed(offer) ? this.labels.claimed : this.labels.locked : this.labels.add)}</button></div>
      </div>`;
    }
    async add(button) {
      if (state.busy) return;
      const product = button.closest('.bogos-gifts__product');
      const variant = product.querySelector('input:checked');
      const offer = offers().find((offer) => String(offer.id) === product.dataset.offer);
      if (!state.cart?.item_count || !variant || variant.disabled || offer?.success !== true || claimed(offer)) return;
      const helper = window.FGSECOMAPP?.helper;
      const addGift = state.addGift || helper?.addGiftToCart?.bind(helper);
      if (!addGift) { this.status(this.labels.unavailable); return; }
      state.busy = true;
      document.querySelectorAll('[data-add-gift]').forEach((element) => { element.disabled = true; });
      button.textContent = this.labels.adding;
      this.status('');
      try {
        const result = await addGift(Number(variant.value), 1, String(offer.id));
        if (result === false) throw new Error('Gift rejected');
        // BOGOS can return before Shopify recalculates the gift discount.
        const giftVariant = offer.gifts.flatMap((gift) => gift.variants || []).find((item) => String(item.id) === variant.value);
        const expectedPrice = Number(giftVariant?.price ?? 0) * 100;
        let priced = false;
        for (let attempt = 0; attempt < 12; attempt++) {
          const cart = await readCart();
          priced = cart.items.some((item) => String(item.variant_id) === variant.value && isGift(item) && item.final_price <= expectedPrice);
          if (priced) break;
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
        if (refreshRequest) await refreshRequest;
        await refreshCarts();
        if (!state.cart?.items?.some((item) => String(item.variant_id) === variant.value && isGift(item))) throw new Error('Gift not added');
        views.forEach((view) => view.status(priced ? view.labels.added : view.labels.syncing));
      } catch {
        views.forEach((view) => view.status(view.labels.error));
      } finally {
        state.busy = false;
        document.querySelectorAll('[data-add-gift]').forEach((element) => {
          const chosen = element.closest('.bogos-gifts__product').querySelector('input:checked');
          const canClaim = element.closest('.bogos-gifts__product').dataset.canClaim === 'true';
          element.disabled = !chosen || chosen.disabled || !canClaim;
          if (canClaim) element.textContent = this.labels.add;
        });
      }
    }
  }
  if (!customElements.get('bogos-gifts')) customElements.define('bogos-gifts', BogosGifts);
  ['fg-today-offer:render', 'fg-gifts:show-slider', 'fg-today-offer:success'].forEach((name) =>
    document.removeEventListener(name, window.ditchBogosCapture));
  (window.ditchBogosEvents || []).forEach((event) => document.dispatchEvent(new CustomEvent(event.type, { detail: event.detail })));
  delete window.ditchBogosEvents;
  delete window.ditchBogosCapture;
})();
